import type { Evidence } from "../evidence.ts";
import type { EmptyAccessDelegation, EmptyAccesses } from "../lifecycle.ts";
import type {
  AgenticContext,
  ApprovalDetails,
  Permission,
  StagedPermission,
} from "../permission.ts";
import type { Access } from "./access.ts";
import type { Delegate } from "./delegate.ts";
import type { Parameter, ParameterType, SpecToType } from "./parameter.ts";

/** The persisted record for an un-staged access request in the Firestore
 * permission-requests collection (minus the access-type field).
 *
 * {@link AccessRequestOf} is the same shape, tagged with which access it is.
 *
 * @example
 * ```ts
 * const example: RequestOf<DbAccesses, "role"> = {
 *
 *   // DelegatesOf<inferred D from DbAccesses["role"]>
 *   delegation: [
 *     {
 *       key: "gcp-cloudsql",
 *       request: {
 *         type: "gcp-cloudsql",
 *         access: "resource",
 *         delegation: [
 *           {
 *             key: "gcloud",
 *             request: {
 *               type: "gcloud",
 *               access: "role",
 *               delegation: [],
 *               permission: { ... },
 *               generated: {},
 *             },
 *           },
 *         ],
 *         permission: { ... },
 *         generated: {},
 *       },
 *     },
 *   ],
 *
 *   // PermissionOf<DbAccesses["role"]
 *   permission: { ... },
 *
 *   // Other various fields, including the principal and request state
 *   principal: "jane@acme.com",
 *   status: "APPROVED",
 *   approvalDetails: { ... },
 *   ...
 * };
 * ```
 */
export type RequestOf<A> =
  A extends Access<infer _G, infer P, infer D>
    ? {
        readonly agentic?: AgenticContext;
        readonly delegation: DelegatesOf<D>;
        readonly evidence?: Evidence[];
        readonly expiryTimestamp?: number;
        /** Concrete permission request parameter values */
        readonly permission: SpecToType<P>;
        readonly principal: string;
        readonly requestedTimestamp: number;
        readonly requestId: string;
        readonly requestor: string;
        readonly status: string;
      }
    : never;

/** Same as {@link RequestOf}, tagged with which access type it is. */
export type AccessRequestOf<
  Accesses,
  K extends keyof Accesses & string = keyof Accesses & string,
> = K extends keyof Accesses & string
  ? RequestOf<Accesses[K]> & { access: K }
  : never;

/** The persisted record for a staged access request in the Firestore
 * permission-requests collection (apart from the access-type field).
 *
 * {@link StagedAccessRequestOf} is the same shape, tagged with which access it is.
 * See the example above.
 */
export type StagedRequestOf<A> =
  A extends Access<infer G, infer _P, infer D>
    ? Omit<RequestOf<A>, "delegation"> & {
        delegation: StagedDelegatesOf<D>;
        generated: SpecToType<G>;
        approvalDetails?: ApprovalDetails;
      }
    : never;

/** Same as {@link StagedRequestOf}, tagged with which access type it is. */
export type StagedAccessRequestOf<
  Accesses,
  K extends keyof Accesses & string = keyof Accesses & string,
> = K extends keyof Accesses & string
  ? StagedRequestOf<Accesses[K]> & { access: K }
  : never;

export type AnyRequestOf<A> = RequestOf<A> | StagedRequestOf<A>;

/** Type guard to convert {@link Permission} objects to individual {@link AccessRequestOf} types
 *
 * Note that this only evaluates the type and otherwise performs no schema validation.
 */
export const isAccessRequestOf = <
  Type extends string,
  Accesses extends EmptyAccesses,
>(
  type: Type,
  value: Permission
): value is AccessRequestOf<Accesses, keyof Accesses & string> & {
  type: Type;
} => value.type === type;

/** Type guard to convert {@link StagedPermission} objects to individual {@link StagedAccessRequestOf} types
 *
 * Note that this only evaluates the type and otherwise performs no schema validation.
 */
export const isStagedAccessRequestOf = <
  Type extends string,
  Accesses extends EmptyAccesses,
>(
  type: Type,
  value: StagedPermission
): value is StagedAccessRequestOf<Accesses> & { type: Type } =>
  value.type === type;

/** One delegate's own request payload — the `request` field on each entry of
 * {@link DelegatesOf}. Directly part of the persisted request document (the
 * nested `delegation[].request` values in the example permission request document
 * at the top of this file).
 */
type RequestDelegateOf<Deleg> =
  Deleg extends Delegate<infer Accesses, infer K>
    ? Accesses[K] extends Access<
        infer _Generated,
        infer Params,
        infer Delegation
      >
      ? {
          /** Integration that fulfills this delegate (the delegate's `resource`)
           *
           * Written at creation so that later phases resolve the delegate without
           * re-reading the parent's delegation config. Absent on Requests created
           * before this field existed.
           */
          type?: string;
          /** Access key within the delegate's access */
          access: K;
          delegation: DelegatesOf<Delegation>;
          permission: SpecToType<Params>;
        }
      : never
    : never;

/** The `delegation` field's type on {@link RequestOf} — one entry per named
 * delegate, each carrying that delegate's own request via {@link RequestDelegateOf}.
 * Directly part of the persisted request document — the example permission request
 * document at the top of this file.
 */
export type DelegatesOf<Delegation extends EmptyAccessDelegation> = {
  [Key in keyof Delegation & string]: {
    /** Name assigned to this delegate by the parent access */
    key: Key;
    request: RequestDelegateOf<Delegation[Key]>;
  };
}[keyof Delegation & string][];

/** Staged counterpart of {@link RequestDelegateOf} (adds `generated`).
 * {@link StagedDelegatesOf}.
 */
type StagedRequestDelegateOf<Deleg> =
  Deleg extends Delegate<infer Accesses, infer K>
    ? Accesses[K] extends Access<
        infer Generated,
        infer Params,
        infer Delegation
      >
      ? {
          type?: string;
          access: K;
          delegation: StagedDelegatesOf<Delegation>;
          permission: SpecToType<Params>;
          generated: SpecToType<Generated>;
        }
      : never
    : never;

/** The `delegation` field's type on {@link StagedRequestOf} — the staged
 * counterpart of {@link DelegatesOf}. Directly part of the persisted, staged
 * request document — see the `delegation` array in the example permission
 * request document at the top of this file.
 */
export type StagedDelegatesOf<Delegation extends EmptyAccessDelegation> = {
  [Key in keyof Delegation & string]: {
    /** Name assigned to this delegate by the parent access */
    key: Key;
    request: StagedRequestDelegateOf<Delegation[Key]>;
  };
}[keyof Delegation & string][];

type UserSuppliedKeys<T extends Record<string, Parameter>> = {
  [K in keyof T]: T[K]["type"] extends "generated" | "object" ? never : K;
}[keyof T];

export type TypedRequest = { type: string };

/** The user-supplied arguments for requesting an access: its parameters,
 * excluding any that are `generated` or `object`-typed
 *
 * @example
 * ```typescript
 * const role = Access({
 *   label: "...",
 *   description: "...",
 *   delegation: { ... },
 *   generated: { ... }
 *   parameters: {
 *     instance: { type: "static", label: "...", optional: true },
 *     instanceId: { type: "generated", label: "..." },
 *     database: { type: "static", label: "...", description: "...", optional: true },
 *     name: { type: "dynamic", label: "..." },
 *   },
 *   render: { ... },
 * })
 *
 * ArgsOf<typeof role> = {
 *   readonly instance: string | undefined;
 *   readonly database: string | undefined;
 *   readonly name: string;
 * };
 * ```
 */
export type ArgsOf<A> =
  A extends Access<infer _G, infer P, infer _D>
    ? {
        readonly [K in UserSuppliedKeys<P>]: P[K] extends Parameter
          ? ParameterType<P[K]>
          : never;
      }
    : never;

/** The permission-parameter portion of an access's request
 *
 * @example
 * ```typescript
 * const role = Access({
 *   label: "...",
 *   description: "...",
 *   maturity: "preview",
 *   delegation: { ... },
 *   generated: { ... },
 *   parameters: {
 *     instance: { type: "static", label: "...", optional: true },
 *     instanceId: { type: "generated", label: "..." },
 *     database: { type: "static", label: "...", description: "...", optional: true },
 *     name: { type: "dynamic", label: "..." },
 *   },
 *   render: { ... },
 * })
 *
 * PermissionOf<typeof role> = {
 *   readonly instance: string | undefined;
 *   readonly instanceId: string;
 *   readonly database: string | undefined;
 *   readonly name: string;
 * };
 * ```
 */
export type PermissionOf<A extends Access<any, any, any>> =
  RequestOf<A>["permission"];
